import React, { useState, useEffect } from "react";
import Papa from "papaparse";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Paper,
  LinearProgress
} from "@mui/material";
import { useDispatch } from "react-redux";
import { uploadBatchCsv } from "../../../../../Reducer/productBatchesSlice";
import { toast } from "react-toastify";

const API_KEY = "68c41af92c786"; // 🔹 replace with your key

// 🔹 Normalize Debounce status
const normalizeStatus = (status) => {
  if (!status) return "error";

  const cleanStatus = status.toLowerCase().trim();

  const safeStatuses = ["safe", "safe to send", "valid", "deliverable", "ok"];
  const invalidStatuses = ["invalid", "undeliverable", "bad"];
  const riskyStatuses = ["risky", "catch-all", "disposable"];

  if (safeStatuses.includes(cleanStatus)) return "Safe";
  if (invalidStatuses.includes(cleanStatus)) return "Invalid";
  if (riskyStatuses.includes(cleanStatus)) return "Risky";

  return status; // fallback if unknown
};

// 🔹 Sleep helper for delay
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const EmailVerifierCsv = ({ file, onClose, batch }) => {
  const [records, setRecords] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const [verifiedRecords, setVerifiedRecords] = useState([]);
  const [progress, setProgress] = useState(0);
  const dispatch = useDispatch();

  // Step 1: Parse CSV
  useEffect(() => {
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          setRecords(result.data);
        }
      });
    }
  }, [file]);

  // Step 2: Verify emails with rate limiting + caching
  const verifyEmails = async () => {
    setVerifying(true);
    setProgress(0);

    const results = [];
    const cache = new Map();
    const concurrency = 2; // max 5 requests at a time
    const delay = 400; // ms delay between batches

    const emails = records.filter((r) => r.email);

    for (let i = 0; i < emails.length; i += concurrency) {
      const batch = emails.slice(i, i + concurrency);

      const promises = batch.map(async (row) => {
        if (cache.has(row.email)) {
          return { ...row, verification: cache.get(row.email) };
        }
        try {
          const res = await axios.get(
            `https://api.debounce.io/v1/?api=${API_KEY}&email=${row.email}`
          );
          const rawStatus = res.data?.debounce?.result || "error";
          const normalized = normalizeStatus(rawStatus);

          cache.set(row.email, normalized);
          return { ...row, verification: normalized };
        } catch {
          return { ...row, verification: "error" };
        }
      });

      const batchResults = await Promise.all(promises);
      results.push(...batchResults);

      setProgress(Math.round((results.length / emails.length) * 100));

      // small delay before next batch
      if (i + concurrency < emails.length) {
        await sleep(delay);
      }
    }

    setVerifiedRecords(results);
    setVerifying(false);
  };

  // Step 3: Upload either all or verified only
  const handleUpload = async (onlyVerified = false) => {
    // If verification was run, embed varified status in each row before uploading
    let finalData;
    if (verifiedRecords.length > 0) {
      const filtered = onlyVerified
        ? verifiedRecords.filter((r) => r.verification === "Safe")
        : verifiedRecords;

      // Map verification result → varified boolean (true/false/null)
      finalData = filtered.map((r) => {
        const { verification, ...rest } = r; // strip the UI-only field
        let varified = null;
        if (verification === "Safe") varified = true;
        else if (verification === "Invalid" || verification === "Risky") varified = false;
        // "error" / unknown → null (leave unverified)
        return { ...rest, varified };
      });
    } else {
      // No verification run — upload raw records as-is (varified remains null in DB)
      finalData = records;
    }

    if (!finalData.length) {
      toast.error("No Records To Upload");
      return;
    }

    // Convert finalData back to CSV string
    const csvString = Papa.unparse(finalData);

    // Convert to File object (so it works like original file upload)
    const newFile = new File([csvString], "verified.csv", {
      type: "text/csv"
    });

    // Dispatch upload
    try {
      await dispatch(uploadBatchCsv({ batch, file: newFile })).unwrap();
      toast.success("Records Uploaded Successfully");
      onClose();
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("Upload Failed");
    }
  };

  // 🔹 Count summaries
  const safeCount = verifiedRecords.filter((r) => r.verification === "Safe")
    .length;
  const invalidCount = verifiedRecords.filter(
    (r) => r.verification === "Invalid"
  ).length;
  const riskyCount = verifiedRecords.filter((r) => r.verification === "Risky")
    .length;

  return (
    <Paper sx={{ mt: 3, p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Verify Emails in CSV
      </Typography>

      {!verifiedRecords.length ? (
        <Box>
          <Button
            variant="contained"
            onClick={verifyEmails}
            disabled={verifying}
          >
            {verifying ? <CircularProgress size={20} /> : "Start Verification"}
          </Button>
          {verifying && (
            <Box mt={2}>
              <Typography variant="body2">
                Verifying... {progress}%
              </Typography>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}
        </Box>
      ) : (
        <>
          {/* 🔹 Summary Chips */}
          <Box mb={2}>
            <Chip label={`Safe: ${safeCount}`} color="success" sx={{ mr: 1 }} />
            <Chip label={`Invalid: ${invalidCount}`} color="error" sx={{ mr: 1 }} />
            <Chip label={`Risky: ${riskyCount}`} color="warning" />
          </Box>

          <Box my={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleUpload(false)}
              sx={{ mr: 2 }}
            >
              Upload Complete CSV
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => handleUpload(true)}
            >
              Upload Only Verified Records
            </Button>
          </Box>

          {/* Preview Table */}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {verifiedRecords.slice(0, 10).map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.verification}
                      color={
                        row.verification === "Safe"
                          ? "success"
                          : row.verification === "Invalid"
                            ? "error"
                            : row.verification === "Risky"
                              ? "warning"
                              : "default"
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary">
            Showing first 10 records
          </Typography>
        </>
      )}

      <Box mt={2}>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </Box>
    </Paper>
  );
};

export default EmailVerifierCsv;
